/**
 * 避難先マスターの避難先 → 描画用「範囲ポリゴン」の解決。
 *
 * 優先順 (この順序が肝):
 *   1. 実形状        … 公式ポリゴン (東京都の広域避難場所SHP 等) / 焼き込み済み
 *   2. Mapbox        … **敷地 (landuse) > 建物** を Mapbox タイルから取得。
 *                       Overpass (公開サーバ) が 429/スロットルで不安定なのに対し、
 *                       Mapbox タイルは即時・無制限に読めるので、実行時の主力にする。
 *                       避難場所の実体は校地・園地なので敷地を建物より優先する。
 *   3. GSI 建物      … 基盤地図情報の建築物面 (内包 > 近傍30m)。Mapbox/OSM に無い
 *                       建物もほぼ全国網羅。点が庭に落ちる施設も近傍で救う。
 *   4. OSM 名称取得   … 施設名で敷地・建物形状を引く (landmarkNames)
 *   5. OSM 座標内包   … 名称で引けない施設を、点を内包する OSM 敷地/建物から拾う
 *   6. 簡易円        … 上記いずれも取れない時だけの最終手段 (approxShape)
 *
 * 背景: 元データ (国土地理院/国土数値情報) は点しか持たないため、OSM に形状が無い避難先は
 * 暫定の簡易円を持つ。これを実形状と同一視すると「円がある → 取得済み」と見なされて
 * 実形状取得が一切走らず、避難先が永久に円のままになる。呼び出し側で分岐を書くと同じ誤りを
 * 繰り返すので、この判定はここ1箇所に閉じ込める。
 *
 * Mapbox を足した理由: 京橋区民館のように「無名の building に内包された点」の施設は
 * OSM 名称では引けず、Overpass の座標内包も 429 で失敗しがちだった。Mapbox タイルなら
 * 同じ点を確実・即時に拾える。敷地 (landuse) を建物より先に見るので、学校は校地全体・
 * 公園は園地が返る (敷地が無い都心ビルだけ建物になる)。
 */
import {
  fetchAreaPolygonByLandmarks,
  fetchFacilityFootprint,
} from '@/services/overpass';
import { fetchMapboxFootprint } from '@/services/mapbox-footprint';
import { maxSiteAreaM2 } from '@/lib/evacuation/site-size';
import { fetchGsiBuilding } from '@/services/gsi-footprint';
import type { EvacuationPlaceExtended } from '@/services/evacuationData';
import type { LngLat } from '@/types/property';
import { nearestRing } from '@/lib/geo/route-postprocess';
import { holesWithin } from '@/lib/geo/polygon-holes';

/** 塗りエリア (外周ring) の配列。1エリアなら [ring]。 */
export type PolygonRings = Array<Array<[number, number]>>;

/**
 * 避難先の範囲ポリゴンを解決する。取得できなければ undefined。
 *
 * @param place マスターの避難先
 * @param fallbackCenter OSM 検索の中心 (place.point が無い時に使う。通常は物件座標)
 */
export async function resolvePlacePolygon(
  place: EvacuationPlaceExtended,
  fallbackCenter: LngLat,
): Promise<PolygonRings | undefined> {
  // 1. 実形状をそのまま採用 (approxShape が無い = 円ではない)
  if (!place.approxShape && place.polygon?.coordinates?.[0]?.length) {
    return place.polygon.coordinates.map((ring) =>
      ring.map(([lng, lat]) => [lng, lat] as [number, number]),
    );
  }

  // 2. Mapbox タイル (敷地 > 建物。Overpass に依らず即時・確実)。
  //    敷地も建物も無い点 (空地・境内の奥) は null → GSI へ。
  const probeCenter = place.point ?? fallbackCenter;
  try {
    // 公園の中の学校などが公園全体を拾わないよう、名前の種類で敷地の大きさを制限する。
    const ring = await fetchMapboxFootprint(probeCenter, {
      maxGroundsAreaM2: maxSiteAreaM2(place.name),
    });
    if (ring && ring.length >= 3) return [ring];
  } catch {
    // プローブ障害 → 次段へフォールバック
  }

  // 3. GSI (基盤地図情報) の建物。Mapbox に無い建物の受け皿 (内包 > 近傍30m)。
  try {
    const ring = await fetchGsiBuilding(probeCenter);
    if (ring && ring.length >= 3) return [ring];
  } catch {
    // プローブ障害 → OSM へフォールバック
  }

  // 4. 円しか無い (or ポリゴン自体が無い) → OSM で実形状を試す。
  //    landmarkNames が無いマスターでも施設名で引けるようフォールバックする。
  const names =
    place.landmarkNames && place.landmarkNames.length > 0
      ? place.landmarkNames
      : place.name
        ? [place.name]
        : [];
  if (names.length > 0) {
    try {
      const ring = await fetchAreaPolygonByLandmarks({
        center: place.point ?? fallbackCenter,
        radiusMeters: 800,
        landmarkNames: names,
      });
      if (ring && ring.length >= 3) return [ring];
    } catch {
      // OSM が落ちていても選択自体は続行 (次段へ)
    }
  }

  // 5. 名称で引けなくても、点を内包する OSM 敷地/建物なら拾える
  //    (Mapbox が建物を返さなかった時の保険。中心は施設の点を使う)。
  const containCenter = place.point ?? fallbackCenter;
  try {
    const ring = await fetchFacilityFootprint(containCenter);
    if (ring && ring.length >= 3) return [ring];
  } catch {
    // Overpass 障害 → 6. の円にフォールバック
  }

  // 6. 最終手段: 簡易円 (無ければ形状なし)
  if (place.polygon?.coordinates?.[0]?.length) {
    return place.polygon.coordinates.map((ring) =>
      ring.map(([lng, lat]) => [lng, lat] as [number, number]),
    );
  }
  return undefined;
}

/** v1 (EvacuationLink.polygon) の範囲 = 単一エリア + そのエリアの除外区域。 */
export interface PlaceArea {
  coordinates: Array<[number, number]>;
  holes?: Array<Array<[number, number]>>;
}

/**
 * v1 (EvacuationLink.polygon) 用に、範囲を**1エリア**で解決する。
 *
 * 複数エリアの避難先 (例: 駒場東大一帯=3) は、物件 (fallbackCenter) に**最も近いエリア**を
 * 採る — v2 が経路を向けるエリアと同じ。以前は並びの先頭を採っており、物件から遠い
 * エリアを描くことがあった。除外区域 (都の広域避難場所の穴。例: 江戸川南部一帯の
 * 新左近川の水面) のうち、そのエリアの中にあるものも返す (塗らない・経路を入れない)。
 */
export async function resolvePlaceArea(
  place: EvacuationPlaceExtended,
  fallbackCenter: LngLat,
): Promise<PlaceArea | undefined> {
  const rings = await resolvePlacePolygon(place, fallbackCenter);
  if (!rings || rings.length === 0) return undefined;
  const ring = nearestRing(fallbackCenter, { coordinates: rings }) ?? rings[0]!;
  const holes = place.approxShape ? [] : holesWithin(ring, place.polygon?.holes);
  return { coordinates: ring, ...(holes.length > 0 ? { holes } : {}) };
}

/** v1 の範囲を、経路処理 (fetchAccurateRoute 等) が受け取るエリア配列の形にする。 */
export function areaAsRings(
  area: PlaceArea | undefined,
): { coordinates: PolygonRings; holes?: PolygonRings } | undefined {
  if (!area) return undefined;
  return {
    coordinates: [area.coordinates],
    ...(area.holes && area.holes.length > 0 ? { holes: area.holes } : {}),
  };
}
